import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Select, Stack, Text, themeVars } from '@eds-web/react';

import { usePayoutForm } from '../../hooks/usePayoutForm';
import { PayPalAccount } from './PayPalAccount';
import { BankAccount } from './BankAccount';
import { GATEWAY_TYPE } from '../../types/payout.types';
import { Loading as LoadingComponent } from '../common/Loading';

export const PayoutView: React.FC = () => {
    const { t } = useTranslation();
    const {
        formik,
        gatewayTypeOptions,
        loading,
        updating,
        handleGatewayTypeChange,
        handleChangeFormValues,
        selectedGatewayType,
        isSubmitDisable,
        isEditModeDisable
    } = usePayoutForm();

    const renderAccountForm = () => {
        if (formik.values.gatewayType === GATEWAY_TYPE.PAYPAL) {
            return (
                <PayPalAccount
                    formik={formik}
                    disabled={isEditModeDisable}
                    handleChangeFormValues={handleChangeFormValues}
                />
            );
        } else if (formik.values.gatewayType === GATEWAY_TYPE.WIRE_TRANSFER) {
            return (
                <BankAccount
                    formik={formik}
                    disabled={isEditModeDisable}
                    handleChangeFormValues={handleChangeFormValues}
                />
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Box
                style={{
                    padding: '128px 0',
                    maxWidth: '800px',
                    margin: '0 auto',
                    textAlign: 'center'
                }}
            >
                <LoadingComponent />
            </Box>
        );
    }

    return (
        <Box style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
            <Stack gap={24}>
                <Box>
                    <Text
                        variant="headingLG"
                        style={{ color: themeVars.color.transparent.light['100'] }}
                    >
                        {t('epic.esport.label.payout_methods')}
                    </Text>
                    <Text
                        variant="paragraphMD"
                        style={{ color: themeVars.color.transparent.light['065'] }}
                    >
                        {t('epic.esport.label.payout_methods_info')}
                    </Text>
                </Box>

                <form noValidate onSubmit={formik.handleSubmit}>
                    <Stack gap={16}>
                        {/* Gateway Type Selection */}
                        <Box style={{ marginBottom: 16 }}>
                            <Select
                                label={t('epic.esport.label.payoutInfo.gatewayType')}
                                name="gatewayType"
                                value={selectedGatewayType}
                                onChange={handleGatewayTypeChange}
                                options={gatewayTypeOptions}
                                disabled={isEditModeDisable}
                                error={formik.touched.gatewayType && formik.errors.gatewayType}
                            />
                        </Box>

                        {renderAccountForm()}

                        <Box style={{ marginTop: 12 }}>
                            <Button
                                type="submit"
                                variant="cta"
                                disabled={isSubmitDisable}
                                loading={updating}
                            >
                                {t('epic.esport.button.submit.account')}
                            </Button>
                        </Box>

                        <Box>
                            <Text
                                variant="paragraphSM"
                                style={{ color: themeVars.color.transparent.light['065'] }}
                            >
                                <a
                                    href="https://www.epicgames.com/site/en-US/privacypolicy"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: themeVars.color.transparent.light['065'] }}
                                >
                                    {t('epic.esport.label.privacyPolicy')}
                                </a>
                            </Text>
                        </Box>
                    </Stack>
                </form>
            </Stack>
        </Box>
    );
};
